import { useRef } from 'react'
import { Check, FileVideo, Image as ImageIcon } from 'lucide-react'
import { Button } from '../common'
import { ActivityIndicator } from '../common/feedback/ActivityIndicator'
import styles from './MediaUploadStatus.module.css'

const PHASE_COPY = {
  queued: ['Waiting to upload', 'Files upload one at a time. You can keep editing your post.'],
  starting: ['Starting upload', 'Connecting to upload storage…'],
  transferring: ['Uploading file', 'Large files can take several minutes, depending on your connection.'],
  reconnecting: ['Reconnecting upload', 'Checking the bytes already received before trying again.'],
  preparing: ['Preparing media', 'File transfer is complete. Waiting for your media to be saved.'],
  analysis_starting: ['Starting video analysis', 'Your media is uploaded. Requesting AI analysis…'],
  failed: ['Upload needs attention', ''],
  canceled: ['Upload canceled', 'Retry this file, or remove it.'],
  interrupted: ['Upload interrupted', 'Choose this file again to upload it. Files cannot resume after closing the window.'],
  ready: ['Media ready', ''],
}

function formatUploadBytes(bytes) {
  if (!Number.isFinite(bytes)) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export default function MediaUploadStatus({ media, job, showAnalysisStage = true, onRetry, onCancel, onRemove, onReplace }) {
  const rootRef = useRef(null)
  const phase = media.uploadPhase || 'ready'
  const analyzing = phase === 'ready' && (job?.status === 'polling' || job?.status === 'retrying')
  const [label, defaultDetail] = PHASE_COPY[phase] || PHASE_COPY.ready
  const stageLabel = analyzing ? (job.status === 'retrying' ? 'Retrying video analysis…' : 'Analyzing video…')
    : phase === 'ready' && job?.status === 'completed' ? 'Video analysis complete' : label
  const detail = analyzing ? 'Your media is uploaded. AI analysis can take several minutes.' : media.uploadError || defaultDetail
  const active = ['starting', 'transferring', 'reconnecting', 'preparing', 'analysis_starting'].includes(phase) || analyzing
  const transferActive = phase === 'transferring' || phase === 'reconnecting'
  const bytes = media.uploadBytes
  const percent = bytes && bytes.total > 0 ? Math.floor((bytes.loaded / bytes.total) * 100) : null
  const failed = phase === 'failed' || phase === 'interrupted'
  const cancelable = ['queued', 'starting', 'transferring', 'reconnecting'].includes(phase)
  const removable = !media.uploading
  const stage = ['queued', 'starting', 'transferring', 'reconnecting', 'failed', 'canceled', 'interrupted'].includes(phase)
    ? 0 : phase === 'preparing' ? 1 : 2
  const perform = (callback) => {
    // Keep focus on this file when retry/cancel replaces its action buttons.
    rootRef.current?.focus()
    callback?.()
  }

  return (
    <div ref={rootRef} tabIndex={-1} className={styles.root} data-error={failed || undefined} aria-label={`Upload status for ${media.name}`}>
      <div className={styles.file}>
        <span className={styles.fileIcon} aria-hidden="true">{media.type === 'video' ? <FileVideo size={22} /> : <ImageIcon size={22} />}</span>
        <div className={styles.fileCopy}>
          <strong className={styles.filename}>{media.name}</strong>
          {media.uploadSize ? <span className={styles.size}>{formatUploadBytes(media.uploadSize)}</span> : null}
        </div>
      </div>
      <div className={styles.status}>
        <div role="status" aria-live="polite" aria-atomic="true" className={styles.announcement}>{`${media.name}: ${stageLabel}${failed && detail ? `. ${detail}` : ''}`}</div>
        <div aria-hidden="true">
          <ActivityIndicator compact status={failed ? 'error' : active ? 'running' : phase === 'ready' ? 'success' : 'waiting'} label={stageLabel} startedAt={active ? analyzing ? job.startedAt : media.uploadStartedAt : null} />
        </div>
        {detail ? <p className={styles.detail}>{detail}</p> : null}
        {transferActive ? (
          <div className={styles.transfer}>
            <div role="progressbar" aria-label={`File transfer for ${media.name}`} aria-valuemin={0} aria-valuemax={100}
              aria-valuenow={percent ?? undefined}
              aria-valuetext={percent === null ? 'Waiting for storage to confirm transferred bytes' : `${percent}%: ${formatUploadBytes(bytes.loaded)} of ${formatUploadBytes(bytes.total)} confirmed`}
              className={styles.track} data-indeterminate={percent === null || undefined}>
              <span style={percent === null ? undefined : { width: `${percent}%` }} />
            </div>
            <span className={styles.bytes}>{percent === null ? 'Waiting for transfer confirmation' : `${formatUploadBytes(bytes.loaded)} of ${formatUploadBytes(bytes.total)} confirmed · ${percent}%`}</span>
          </div>
        ) : null}
        {!failed && !['interrupted', 'canceled'].includes(phase) ? (
          <ol className={styles.stages} aria-label="Media stages">
            {['Transfer', 'Prepare', ...(showAnalysisStage && media.type === 'video' && (!job || !['unavailable', 'too_large', 'invalid_input'].includes(job.status)) ? ['Analyze'] : [])].map((name, index) => (
              <li key={name} data-complete={index < stage || phase === 'ready' && !analyzing || undefined} aria-current={index === stage ? 'step' : undefined}>
                {index < stage || phase === 'ready' && !analyzing ? <Check size={12} aria-hidden="true" /> : <span aria-hidden="true">{index + 1}</span>}
                {name}
              </li>
            ))}
          </ol>
        ) : null}
      </div>
      <div className={styles.actions}>
        {cancelable ? <Button variant="secondary" onClick={() => perform(onCancel)} aria-label={`Cancel upload of ${media.name}`}>Cancel upload</Button> : null}
        {['failed', 'canceled'].includes(phase) && media.uploadCanRetry !== false ? <Button onClick={() => perform(onRetry)} aria-label={`Retry upload of ${media.name}`}>Retry upload</Button> : null}
        {phase === 'failed' && media.uploadCanRetry === false ? <Button onClick={onReplace}>Replace file</Button> : null}
        {phase === 'interrupted' ? <Button onClick={onReplace}>Choose file again</Button> : null}
        {removable ? <Button variant="ghost" onClick={onRemove} aria-label={`Remove ${media.name}`}>Remove</Button> : null}
      </div>
    </div>
  )
}
